import React, { useEffect, useMemo, useRef, useState } from "react";
import { RulerInput, ScreenShell, SegmentedToggle } from "../components";
import { displayWeight, parseWeightToLbs, weightMax, weightMin, weightStep } from "../units";
import type { ImportFromHealthOutcome, WeightUnit } from "../types";
import { T } from "../theme";

type Props = {
  step: number;
  total: number;
  currentWeightLbs: number;
  unit: WeightUnit;
  onWeightChange: (lbs: number) => void;
  onUnitChange: (u: WeightUnit) => void;
  onNext: () => void;
  onBack: () => void;
  isMobile: boolean;
  isNativeIOS: boolean;
  /** Used only by the silent auto-populate path when Apple Health is already
   *  connected — there is no manual import button on this screen anymore.
   *  HealthKit gets its own pitch earlier in the flow. */
  onImportFromHealth?: () => Promise<ImportFromHealthOutcome>;
  /** When true at first render, run the import once on mount. Captured from
   *  the parent and seeded into local state so a re-render can't refire. */
  autoImportOnMount?: boolean;
};

export function WeightScreen({
  step,
  total,
  currentWeightLbs,
  unit,
  onWeightChange,
  onUnitChange,
  onNext,
  onBack,
  isMobile,
  isNativeIOS: _isNativeIOS,
  onImportFromHealth,
  autoImportOnMount,
}: Props): React.ReactElement {
  const displayedValue = displayWeight(currentWeightLbs, unit);
  const valid = currentWeightLbs > 0 && currentWeightLbs < 1000;

  // Confirm strip shown after a successful auto-import. Lets the user undo
  // before continuing — and auto-dismisses the moment they scrub the ruler.
  const [hkPulled, setHkPulled] = useState<{ priorLbs: number; importedLbs: number } | null>(null);

  useEffect(() => {
    if (hkPulled && currentWeightLbs !== hkPulled.importedLbs) {
      setHkPulled(null);
    }
  }, [currentWeightLbs, hkPulled]);

  // Silent auto-populate. Failures (denied / no data / error) are intentional
  // no-ops: HealthKit was already pitched on its own screen, and the user can
  // always log a weight manually here. We don't surface another error UI.
  const [shouldAutoImport] = useState<boolean>(!!autoImportOnMount);
  useEffect(() => {
    if (!shouldAutoImport || !onImportFromHealth) return;
    const priorLbs = currentWeightLbs;
    void (async () => {
      try {
        const result = await onImportFromHealth();
        if (result.kind === "imported" && result.weightLbs > 0) {
          setHkPulled({ priorLbs, importedLbs: result.weightLbs });
        }
      } catch {
        // Best-effort.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUndoPull = () => {
    if (!hkPulled) return;
    onWeightChange(hkPulled.priorLbs);
    setHkPulled(null);
  };

  // Anchor the ruler range to the value we first rendered with (per unit) so
  // labels don't reshuffle as the user scrubs. Wide enough to cover plausible
  // adjustments without the ticks crowding into the center.
  const seedByUnit = useRef<{ lb: number | null; kg: number | null }>({ lb: null, kg: null });
  if (seedByUnit.current[unit] === null) {
    seedByUnit.current[unit] = displayedValue;
  }
  const seed = seedByUnit.current[unit] ?? displayedValue;
  const range = useMemo(() => {
    const span = unit === "lb" ? 80 : 35;
    const minV = Math.max(weightMin(unit), Math.round(seed - span));
    const maxV = Math.min(weightMax(unit), Math.round(seed + span));
    return { min: minV, max: maxV };
  }, [unit, seed]);

  return (
    <ScreenShell
      step={step}
      total={total}
      title="What's your current weight?"
      subtitle="We'll use this as your starting point."
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="Continue"
      primaryDisabled={!valid}
      onPrimary={onNext}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
        <SegmentedToggle
          options={[
            { value: "lb", label: "Pounds" },
            { value: "kg", label: "Kilograms" },
          ]}
          value={unit}
          onChange={onUnitChange}
        />
      </div>

      <RulerInput
        value={displayedValue}
        min={range.min}
        max={range.max}
        step={weightStep(unit)}
        majorStep={unit === "lb" ? 10 : 5}
        unit={unit}
        decimals={unit === "lb" ? 0 : 1}
        onChange={(v) => onWeightChange(parseWeightToLbs(v, unit))}
        accent="lime"
      />

      {hkPulled ? (
        <div
          style={{
            marginTop: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "0.7rem 0.9rem",
            borderRadius: 12,
            background: "rgba(132,204,22,0.08)",
            border: `1px solid ${T.hairlineStrong}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.lime} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <div style={{ fontSize: "0.85rem", color: T.fg, lineHeight: 1.35, minWidth: 0 }}>
              Pulled {displayWeight(hkPulled.importedLbs, unit)} {unit} from Apple Health
            </div>
          </div>
          <button
            type="button"
            onClick={handleUndoPull}
            style={{
              flexShrink: 0,
              background: "transparent",
              border: "none",
              padding: "0.25rem 0.5rem",
              color: T.fg2,
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Undo
          </button>
        </div>
      ) : null}
    </ScreenShell>
  );
}
